/*
 * The Sphere analytics tracking plan.
 *
 * Conventions:
 * - Event names are "Object Action" in Title Case, past tense (`Robot Command Requested`).
 * - Property names are snake_case.
 * - Every event and property sent to Amplitude is declared here, so the full list of what we
 *   collect can be reviewed in one place against the privacy policy (section 6).
 * - Never add names, email addresses, phone numbers, free text typed by a user, or raw URLs.
 *   Use ids, enum values, route templates and localization keys instead.
 */

export enum AnalyticsEvent {
  PageViewed = 'Page Viewed',
  RobotCommandRequested = 'Robot Command Requested',
  RobotCommandCompleted = 'Robot Command Completed',
  LiveSnapshotRequested = 'Live Snapshot Requested',
  ZonePlanChangeUndone = 'Zone Plan Change Undone',
  ZonePlanChangeRedone = 'Zone Plan Change Redone',
  RobotConnectionLost = 'Robot Connection Lost',
  ApiRequestFailed = 'API Request Failed',

  // Sign-in, registration and account security
  LoginSucceeded = 'Login Succeeded',
  TwoFactorChallengeShown = 'Two Factor Challenge Shown',
  TwoFactorVerified = 'Two Factor Verified',
  TwoFactorSetupStarted = 'Two Factor Setup Started',
  TwoFactorSetupCompleted = 'Two Factor Setup Completed',
  TwoFactorSetupCancelled = 'Two Factor Setup Cancelled',
  TwoFactorDisabled = 'Two Factor Disabled',
  UserTwoFactorReset = 'User Two Factor Reset',
  PasswordChanged = 'Password Changed',
  PasswordResetRequested = 'Password Reset Requested',
  PasswordSet = 'Password Set',
  InviteValidated = 'Invite Validated',
  RegistrationCompleted = 'Registration Completed',

  // Organization and user management
  OrganizationCreated = 'Organization Created',
  OrganizationUpdated = 'Organization Updated',
  OrganizationDeleted = 'Organization Deleted',
  UserInvited = 'User Invited',
  UserUpdated = 'User Updated',
  UserDeleted = 'User Deleted',
  ApiKeyCreated = 'API Key Created',
  ApiKeyDeleted = 'API Key Deleted',
  AccountDataExported = 'Account Data Exported',

  // Software versions
  VersionSetCreated = 'Version Set Created',
  VersionSetUpdated = 'Version Set Updated',
  VersionSetDeleted = 'Version Set Deleted',
  SoftwareVersionCreated = 'Software Version Created',
  SoftwareVersionUpdated = 'Software Version Updated',
  SoftwareVersionDeleted = 'Software Version Deleted',

  // Notifications
  NotificationSubscriptionCreated = 'Notification Subscription Created',
  NotificationSubscriptionUpdated = 'Notification Subscription Updated',
  NotificationSubscriptionDeleted = 'Notification Subscription Deleted',
  RobotSubscriptionToggled = 'Robot Subscription Toggled',
  OrganizationSubscriptionToggled = 'Organization Subscription Toggled',
  PushNotificationsEnabled = 'Push Notifications Enabled',

  // Statistics, insights, datasets and reports
  FiltersChanged = 'Filters Changed',
  InsightsViewed = 'Insights Viewed',
  InsightsDashboardCreated = 'Insights Dashboard Created',
  InsightsDashboardSaved = 'Insights Dashboard Saved',
  InsightsDashboardDeleted = 'Insights Dashboard Deleted',
  DatasetCreated = 'Dataset Created',
  DatasetSaved = 'Dataset Saved',
  DatasetDeleted = 'Dataset Deleted',
  DatasetExported = 'Dataset Exported',
  InsightsDataLoaded = 'Insights Data Loaded',
  ReportPreviewed = 'Report Previewed',
  ReportDownloadRequested = 'Report Download Requested',

  // Zone planner
  ZonePlannerOpened = 'Zone Planner Opened',
  ZonePlanSaved = 'Zone Plan Saved',

  // Map configuration, missions, schedules and robot settings
  MapConfigurationChanged = 'Map Configuration Changed',
  MissionCreated = 'Mission Created',
  MissionUpdated = 'Mission Updated',
  MissionDeleted = 'Mission Deleted',
  ScheduleCreated = 'Schedule Created',
  ScheduleUpdated = 'Schedule Updated',
  ScheduleDeleted = 'Schedule Deleted',
  SchedulesToggled = 'Schedules Toggled',
  RobotSettingChanged = 'Robot Setting Changed',

  // Robot access and sync
  RobotAccessUsed = 'Robot Access Used',
  RobotSyncTimedOut = 'Robot Sync Timed Out',

  // Help and support
  HelpLinkOpened = 'Help Link Opened',
  SupportPopoverOpened = 'Support Popover Opened',
  SupportContactUsed = 'Support Contact Used',

  // Search
  SearchSubmitted = 'Search Submitted',
  SearchResultClicked = 'Search Result Clicked',
  SearchAbandoned = 'Search Abandoned',

  /** Any tracked operation above whose request failed; `operation` names which one. */
  OperationFailed = 'Operation Failed',
}

export enum RobotSettingArea {
  CleaningParameters = 'cleaning_parameters',
  EnvironmentPollution = 'environment_pollution',
  NavigationLocalPlanner = 'navigation_local_planner',
  NavigationTasks = 'navigation_tasks',
  ExcludedDates = 'excluded_dates',
  RoomButtonConfiguration = 'room_button_configuration',
  RobotDetails = 'robot_details',
  AccessSettings = 'access_settings',
}

/**
 * Robot access actions tracked from the access settings and sync buttons. Service connection
 * buttons send their command's ActionType value instead, since the set is defined by the robot.
 */
export enum RobotAccessAction {
  CopyRobotId = 'copy_robot_id',
  CopyAuthToken = 'copy_auth_token',
  RefreshWireguardConfig = 'refresh_wireguard_config',
  DownloadWireguardConfig = 'download_wireguard_config',
  CopyWireguardConfig = 'copy_wireguard_config',
  SyncOpenTasks = 'sync_open_tasks',
  FullDataSync = 'full_data_sync',
  FullDataSyncCancelled = 'full_data_sync_cancelled',
}

export enum HelpLink {
  NewFeatures = 'new_features',
  KnowledgeHub = 'knowledge_hub',
}

export enum HelpLinkSource {
  TopBar = 'top_bar',
  TopBarMenu = 'top_bar_menu',
  SupportPopover = 'support_popover',
}

export enum SupportChannel {
  Email = 'email',
  Phone = 'phone',
}

export enum InsightsDataSource {
  Insights = 'insights',
  Dataset = 'dataset',
}

export enum ReportType {
  MissionSummary = 'mission_summary',
  DashboardSummary = 'dashboard_summary',
}

/** Page names sent with `Page Viewed`; stable, human-readable names rather than URLs. */
export enum PageName {
  Login = 'Login',
  Register = 'Register',
  Unsubscribe = 'Unsubscribe',
  Legal = 'Legal',
  AccountData = 'Account Data',
  Home = 'Home',
  OrganizationDashboard = 'Organization Dashboard',
  Statistics = 'Statistics',
  MissionHistory = 'Mission History',
  Users = 'Users',
  Organizations = 'Organizations',
  OrganizationSettings = 'Organization Settings',
  Administration = 'Administration',
  Insights = 'Insights',
  InsightsDatasets = 'Insights Datasets',
  NotFound = 'Not Found',
}

export enum AppPlatform {
  Browser = 'browser',
  Pwa = 'pwa',
  Native = 'native',
}

export enum CommandOutcome {
  /** The robot answered (an updated action list or live image arrived) within the timeout. */
  Responded = 'responded',
  /** Sent, but no answer from the robot within the timeout. */
  TimedOut = 'timed_out',
  /** Never sent, because the robot connection was not open. */
  NotSent = 'not_sent',
}

interface RobotContext {
  robot_id: string | null;
  robot_serial: string | null;
}

export interface AnalyticsEventProperties extends AnalyticsFeatureEventProperties {
  [AnalyticsEvent.PageViewed]: {
    page_name: string;
    page_path: string;
    page_tab: string | null;
    detail_view: string | null;
  };
  [AnalyticsEvent.RobotCommandRequested]: RobotContext & {
    command_action: string;
    source_page: string;
  };
  [AnalyticsEvent.RobotCommandCompleted]: RobotContext & {
    command_type: string;
    command_action: string | null;
    outcome: CommandOutcome;
    latency_ms: number | null;
  };
  [AnalyticsEvent.LiveSnapshotRequested]: RobotContext & {
    source_page: string;
  };
  [AnalyticsEvent.ZonePlanChangeUndone]: {
    history_action: string | null;
    history_type: string | null;
  };
  [AnalyticsEvent.ZonePlanChangeRedone]: {
    history_action: string | null;
    history_type: string | null;
  };
  [AnalyticsEvent.RobotConnectionLost]: {
    robot_id: string | null;
    close_code: number;
    connected_ms: number;
  };
  [AnalyticsEvent.ApiRequestFailed]: {
    endpoint: string;
    method: string;
    status_code: number | null;
    error_category: string;
    error_keys: string[];
    page_name: string | null;
  };
}

type NoProperties = Record<string, never>;
type SourcePage = { source_page: string | null };

interface FilterProperties extends SourcePage {
  filter_keys: string[];
  date_range_days: number | null;
  filter_action?: string | null;
}

interface MissionProperties {
  robot_id: string | null;
  /** Robot page tab the change was made from, e.g. `missions` or `settings` (button setup). */
  source_tab: string | null;
  changed_fields: string[];
}

interface ScheduleProperties {
  robot_id: string | null;
  changed_fields: string[];
}

/** Events sent by the Redux analytics middleware and search/report/zone components. */
export interface AnalyticsFeatureEventProperties {
  [AnalyticsEvent.LoginSucceeded]: { login_variant: string };
  [AnalyticsEvent.TwoFactorChallengeShown]: { login_variant: string; setup_required: boolean };
  [AnalyticsEvent.TwoFactorVerified]: { method: 'otp' | 'backup_code' };
  [AnalyticsEvent.TwoFactorSetupStarted]: { is_required_at_login: boolean };
  [AnalyticsEvent.TwoFactorSetupCompleted]: { is_required_at_login: boolean };
  [AnalyticsEvent.TwoFactorSetupCancelled]: NoProperties;
  [AnalyticsEvent.TwoFactorDisabled]: { method: string };
  [AnalyticsEvent.UserTwoFactorReset]: NoProperties;
  [AnalyticsEvent.PasswordChanged]: NoProperties;
  [AnalyticsEvent.PasswordResetRequested]: NoProperties;
  [AnalyticsEvent.PasswordSet]: { requires_two_factor: boolean };
  [AnalyticsEvent.InviteValidated]: NoProperties;
  [AnalyticsEvent.RegistrationCompleted]: NoProperties;

  [AnalyticsEvent.OrganizationCreated]: { organization_role: string | null };
  [AnalyticsEvent.OrganizationUpdated]: { changed_fields: string[] };
  [AnalyticsEvent.OrganizationDeleted]: NoProperties;
  [AnalyticsEvent.UserInvited]: { invited_role: string | null };
  [AnalyticsEvent.UserUpdated]: { patch_type: string; changed_fields: string[] };
  [AnalyticsEvent.UserDeleted]: { is_self: boolean };
  [AnalyticsEvent.ApiKeyCreated]: { scope_count: number };
  [AnalyticsEvent.ApiKeyDeleted]: NoProperties;
  [AnalyticsEvent.AccountDataExported]: { is_own_account: boolean };

  [AnalyticsEvent.VersionSetCreated]: NoProperties;
  [AnalyticsEvent.VersionSetUpdated]: { changed_fields: string[] };
  [AnalyticsEvent.VersionSetDeleted]: NoProperties;
  [AnalyticsEvent.SoftwareVersionCreated]: NoProperties;
  [AnalyticsEvent.SoftwareVersionUpdated]: { changed_fields: string[] };
  [AnalyticsEvent.SoftwareVersionDeleted]: NoProperties;

  [AnalyticsEvent.NotificationSubscriptionCreated]: {
    trigger: string | null;
    repeat: string | null;
    email_active: boolean | null;
    push_active: boolean | null;
  };
  [AnalyticsEvent.NotificationSubscriptionUpdated]: {
    changed_fields: string[];
    email_active: boolean | null;
    push_active: boolean | null;
  };
  [AnalyticsEvent.NotificationSubscriptionDeleted]: NoProperties;
  [AnalyticsEvent.RobotSubscriptionToggled]: { is_subscribed: boolean };
  [AnalyticsEvent.OrganizationSubscriptionToggled]: { is_subscribed: boolean };
  [AnalyticsEvent.PushNotificationsEnabled]: NoProperties;

  [AnalyticsEvent.FiltersChanged]: FilterProperties;
  [AnalyticsEvent.InsightsViewed]: {
    view_type: string;
    is_dataset_page: boolean;
    dashboard_id: string | null;
    dataset_id: string | null;
  };
  [AnalyticsEvent.InsightsDashboardCreated]: { view_type: string | null; widget_count: number };
  [AnalyticsEvent.InsightsDashboardSaved]: { view_type: string | null; widget_count: number };
  [AnalyticsEvent.InsightsDashboardDeleted]: NoProperties;
  [AnalyticsEvent.DatasetCreated]: NoProperties;
  [AnalyticsEvent.DatasetSaved]: NoProperties;
  [AnalyticsEvent.DatasetDeleted]: NoProperties;
  [AnalyticsEvent.DatasetExported]: { dataset_id: string; row_count: number; column_count: number };
  [AnalyticsEvent.InsightsDataLoaded]: SourcePage & {
    data_source: InsightsDataSource;
    endpoint: string;
    latency_ms: number;
    payload_bytes: number | null;
    row_count: number | null;
    filter_keys: string[];
    date_range_days: number | null;
  };
  [AnalyticsEvent.ReportPreviewed]: SourcePage & { report_type: ReportType };
  [AnalyticsEvent.ReportDownloadRequested]: SourcePage & { report_type: ReportType };

  [AnalyticsEvent.ZonePlannerOpened]: { robot_id: string | null; robot_serial: string | null };
  [AnalyticsEvent.ZonePlanSaved]: {
    zone_type: string;
    operation: 'created' | 'updated' | 'deleted';
    robot_id: string | null;
  };

  [AnalyticsEvent.MapConfigurationChanged]: {
    target: 'floor' | 'room';
    robot_id: string | null;
    changed_fields: string[];
  };
  [AnalyticsEvent.MissionCreated]: MissionProperties;
  [AnalyticsEvent.MissionUpdated]: MissionProperties;
  [AnalyticsEvent.MissionDeleted]: MissionProperties;
  [AnalyticsEvent.ScheduleCreated]: ScheduleProperties;
  [AnalyticsEvent.ScheduleUpdated]: ScheduleProperties;
  [AnalyticsEvent.ScheduleDeleted]: ScheduleProperties;
  [AnalyticsEvent.SchedulesToggled]: { enabled: boolean; robot_id: string | null };
  [AnalyticsEvent.RobotSettingChanged]: {
    setting_area: RobotSettingArea;
    operation: 'created' | 'updated' | 'deleted';
    robot_id: string | null;
    changed_fields: string[];
  };

  [AnalyticsEvent.RobotAccessUsed]: {
    access_action: string;
    robot_id: string | null;
    robot_serial: string | null;
  };
  [AnalyticsEvent.RobotSyncTimedOut]: { sync_type: RobotAccessAction; robot_id: string | null };

  [AnalyticsEvent.HelpLinkOpened]: SourcePage & { link: HelpLink; source: HelpLinkSource };
  [AnalyticsEvent.SupportPopoverOpened]: SourcePage & { has_email: boolean; has_phone: boolean };
  [AnalyticsEvent.SupportContactUsed]: SourcePage & { channel: SupportChannel };

  [AnalyticsEvent.SearchSubmitted]: SourcePage & { query_length: number; search_fields: string[] };
  [AnalyticsEvent.SearchResultClicked]: SourcePage & {
    result_type: string;
    result_source: 'suggestion' | 'search' | 'history';
    result_position: number;
    result_count: number;
    query_length: number;
    is_embedded: boolean;
  };
  [AnalyticsEvent.SearchAbandoned]: SourcePage & {
    query_length: number;
    result_count: number;
    search_status: string;
  };

  [AnalyticsEvent.OperationFailed]: {
    operation: string;
    status_code: number | null;
  };
}

/** User properties set via identify. Declared here for the same review reason as events. */
export interface AnalyticsUserProfile {
  userId: string;
  userRole: string;
  organizationId: string;
  organizationRole: string;
  organizationRobotCount: number | null;
  language: string;
  loginVariant: string | null;
  isTwoFactorEnabled: boolean;
  accountCreatedAt: string | null;
}
